import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/common/SEO';
import { ROUTES } from '../utils/constants';
import { useTranslation } from 'react-i18next';

export function LandingPage() {
  const { t } = useTranslation('landing');
  const [searchParams, setSearchParams] = useSearchParams();
  const accountDeleted = searchParams.get('deleted') === 'true';

  useEffect(() => {
    if (accountDeleted) {
      // Clear the query param after a short delay so it doesn't persist on refresh.
      const timer = setTimeout(() => setSearchParams({}, { replace: true }), 5000);
      return () => clearTimeout(timer);
    }
  }, [accountDeleted, setSearchParams]);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO />
      <Header />
      {accountDeleted && (
        <div className="bg-green-50 border-b border-green-200 px-6 py-3 text-center text-sm text-green-800">
          Your account has been deleted. We hope to see you again.
        </div>
      )}

      <main className="flex-1 bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 via-primary-50/20 to-white pt-12 pb-8">
        <div className="container mx-auto px-6 text-center max-w-4xl">
          <h1 className="text-4xl font-medium tracking-tight text-neutral-900 mb-3 leading-tight">
            {t('hero.title')}
          </h1>
          <p className="text-body-lg max-w-2xl mx-auto mb-6">
            {t('hero.subtitle')}
          </p>

          <div className="flex gap-3 justify-center mb-4">
            <Link to={ROUTES.REGISTER}>
              <Button size="md" variant="secondary">{t('hero.signUp')}</Button>
            </Link>
            <Link to={ROUTES.DETAILS}>
              <Button variant="ghost" size="md">{t('hero.learnMore')}</Button>
            </Link>
          </div>

          <p className="text-sm font-medium text-accent-600 mb-3">{t('hero.incubation')}</p>

          <div className="flex items-center justify-center gap-2">
            <span>🇪🇺</span>
            <span className="text-sm font-semibold text-neutral-700 tracking-wide">{t('hero.sovereignty')}</span>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-10 bg-white border-t border-neutral-200/60">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-headline text-accent-600 mb-4">{t('vision.title')}</h2>
          <p className="text-body mb-3 max-w-3xl">{t('vision.body1')}</p>
          <p className="text-body mb-6 max-w-3xl">{t('vision.body2')}</p>
          <Link to={ROUTES.DETAILS}>
            <Button variant="secondary" size="sm">{t('vision.readMore')} →</Button>
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 bg-white border-t border-neutral-200/60">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-headline text-accent-600 mb-8">
            {t('features.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-200/60">
            <div className="bg-white px-6 py-6">
              <span className="block text-xs font-semibold tracking-widest text-accent-600 uppercase mb-3">01</span>
              <h3 className="text-title mb-1">{t('features.describe.title')}</h3>
              <p className="text-body">{t('features.describe.description')}</p>
            </div>
            <div className="bg-white px-6 py-6">
              <span className="block text-xs font-semibold tracking-widest text-accent-600 uppercase mb-3">02</span>
              <h3 className="text-title mb-1">{t('features.generate.title')}</h3>
              <p className="text-body">{t('features.generate.description')}</p>
            </div>
            <div className="bg-white px-6 py-6">
              <span className="block text-xs font-semibold tracking-widest text-accent-600 uppercase mb-3">03</span>
              <h3 className="text-title mb-1">{t('features.deploy.title')}</h3>
              <p className="text-body">{t('features.deploy.description')}</p>
            </div>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}
